type StorageRetryAttempt = {
  attempt: number;
  delayMs: number;
  status?: number;
  error?: unknown;
};

type StorageRetryOptions = {
  onRetry?: (retryAttempt: StorageRetryAttempt) => void;
};

type StorageFetchResult =
  | { response: Response; error?: undefined }
  | { response?: undefined; error: unknown };

const STORAGE_RETRY_ATTEMPTS = 5;
const STORAGE_RETRY_BASE_DELAY_MS = 300;
const STORAGE_RETRY_MAX_DELAY_MS = 5000;
const RETRYABLE_STORAGE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

export const isRetryableStorageStatus = (status: number): boolean =>
  RETRYABLE_STORAGE_STATUSES.has(status);

const parseRetryAfterMs = (retryAfter?: string | null): number | undefined => {
  const value = retryAfter?.trim();

  if (!value) {
    return undefined;
  }

  if (/^\d+$/.test(value)) {
    return Number(value) * 1000;
  }

  const retryAt = Date.parse(value);

  return Number.isNaN(retryAt) ? undefined : Math.max(retryAt - Date.now(), 0);
};

export const getStorageRetryDelayMs = ({
  attempt,
  retryAfter,
}: {
  attempt: number;
  retryAfter?: string | null;
}): number | null => {
  if (attempt >= STORAGE_RETRY_ATTEMPTS) {
    return null;
  }

  const backoffMs =
    Math.random() *
    Math.min(
      STORAGE_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
      STORAGE_RETRY_MAX_DELAY_MS,
    );
  const retryAfterMs = parseRetryAfterMs(retryAfter);

  if (retryAfterMs === undefined) {
    return backoffMs;
  }

  return retryAfterMs > STORAGE_RETRY_MAX_DELAY_MS
    ? null
    : Math.max(backoffMs, retryAfterMs);
};

const waitForStorageRetry = (delayMs: number, signal?: AbortSignal | null) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const handleAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);

    signal?.addEventListener('abort', handleAbort, { once: true });
  });

const fetchStorageAttempt = async ({
  input,
  init,
  attempt,
  onRetry,
}: {
  input: string;
  init: RequestInit;
  attempt: number;
  onRetry?: StorageRetryOptions['onRetry'];
}): Promise<Response> => {
  const { response, error } = await fetch(input, init).then(
    (fetchedResponse): StorageFetchResult => ({ response: fetchedResponse }),
    (fetchError: unknown): StorageFetchResult => ({ error: fetchError }),
  );

  if (response && !isRetryableStorageStatus(response.status)) {
    return response;
  }

  const delayMs = init.signal?.aborted
    ? null
    : getStorageRetryDelayMs({
        attempt,
        retryAfter: response?.headers.get('Retry-After'),
      });

  if (delayMs === null) {
    if (response) {
      return response;
    }

    throw error;
  }

  await response?.body?.cancel();
  onRetry?.({ attempt, delayMs, status: response?.status, error });
  await waitForStorageRetry(delayMs, init.signal);

  return fetchStorageAttempt({ input, init, attempt: attempt + 1, onRetry });
};

export const fetchWithStorageRetry = (
  input: string,
  init: RequestInit = {},
  { onRetry }: StorageRetryOptions = {},
): Promise<Response> =>
  fetchStorageAttempt({ input, init, attempt: 1, onRetry });
